<template>
  <section class="bag-size-section">
    <h5 class="single-page-signup-title">
      Choose your bag size
    </h5>

    <div class="row mx-0">
      <div
        v-for="(plan, index) in coffee_plans"
        :key="plan.coffee_plan_sku"
        :class="{
          'col-6 m-auto': true,
          'pl-0 pr-2': index === 0,
          'pl-2 pr-0': index === 1
        }"
      >
        <div
          :class="{
            'single-page-signup-option hand-pointer t-center': true,
            picked: userData.coffee.sku !== undefined && coffee_to_use.sku === userData.coffee.sku.split('-')[0] && userData.amount === plan.amount
          }"
          @click="selectPlan(plan)"
        >
          <div v-if="plan.amount === 1000" class="option-roundel best-value d-flex">
            <span>Best value</span>
          </div>
          <p class="single-page-signup-option-text amount mb-0">
            {{ plan.amount_display }}
          </p>
          <p v-if="plan.discounted_price === null && !userData.is_pioneer" class="col-12 single-page-signup-option-text price mb-0">
            £{{ parseFloat(plan.price).toFixed(2) }}
          </p>
          <div v-else class="col-12 px-0 row mx-0">
            <span class="col-12 px-0 single-page-signup-option-text discounted">£{{ parseFloat(planDiscountedPrice(plan)).toFixed(2) }}</span>
            <span class="col-12 px-0 single-page-signup-option-text old-price">was (
              <span style="text-decoration-line: line-through;">£{{ parseFloat(plan.price).toFixed(2) }}</span>)
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { inject, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import * as GTM from '../../../utils/analytics/gtm'

const store = useStore()
const funnel_version = inject('funnel_version')
const userData = inject('userData')
const updateUserData = inject('updateUserData')
const setPlanCache = inject('setPlanCache')

const coffees_range = computed(() => {
  return store.getters['shop/coffees_range']
})

const coffee_to_use = computed(() => {
  // default show lowest coffee
  var coffee = undefined
  if (userData.value?.coffee?.sku !== undefined) coffee = coffees_range.value.find((coffee) => coffee.sku === userData.value.coffee.sku.split('-')[0])
  return coffee !== undefined ? coffee : getCheapest250gCoffee(coffees_range.value)
})

const coffee_plans = computed(() => {
  var plans = []
  coffee_to_use.value?.coffee_plans.forEach((plan) => {
    const allowed_plans_amounts = [ '250g', '1Kg' ]
    if(allowed_plans_amounts.includes(Object.keys(plan)[0]))
      plans.push({ ...plan[Object.keys(plan)[0]], amount_display: Object.keys(plan)[0].toLowerCase() })
  })
  return plans.sort((a, b) => {
    if(a.amount > b.amount) return 1
    else if(a.amount < b.amount) return -1
    return 0
  })
})

function getCheapest250gCoffee(coffees) {
  let cheapestCoffee = undefined
  let cheapestPrice = Infinity
  coffees.forEach((coffee) => {
    coffee.coffee_plans.forEach((planGroup) => {
      const plan250g = planGroup['250g']
      if (plan250g && plan250g.price < cheapestPrice) {
        cheapestCoffee = coffee
        cheapestPrice = plan250g.price
      }
    })
  })

  return cheapestCoffee
}


function planDiscountedPrice(plan){
  if( userData.value.is_pioneer && plan.discounted_price !== null ){
    return plan.pioneer_discounted_price
  } else return userData.value.is_pioneer ? plan.pioneer_price : plan.discounted_price
}

function planCoffeeSku(plan, sku) {
  if (!sku) return undefined
  var variant_suffix = plan.amount === 1000 ? '-G1000' : '-G250'
  var coffee_sku = sku.split('-')[0] + variant_suffix
  return coffee_sku
}

function selectPlan(plan) {
  if (!userData.value?.plan?.sku){
    store.dispatch('notifications/showNotification', { type: 'error', message: 'You have to choose a coffee first' })
    return
  }
  if(coffee_to_use.value.sku === userData.value.coffee.sku.split('-')[0] && userData.value.amount === plan.amount){
    var cheap_plan = coffee_plans.value.find((plan) => plan.amount === 250)
    const plan_price = cheap_plan.discounted_price !== null ? cheap_plan.discounted_price : cheap_plan.price
    const plan_pioneer_price = cheap_plan.pioneer_discounted_price !== null ? cheap_plan.pioneer_discounted_price : cheap_plan.pioneer_price
    updateUserData({
      plan: {
        name: cheap_plan.plan,
        sku: cheap_plan.coffee_plan_sku,
        price: cheap_plan.price,
        pioneer_price: cheap_plan.pioneer_price,
        coffee_type: userData.value.coffee_type,
        discounted_price: cheap_plan.discounted_price,
        pioneer_discounted_price: cheap_plan.pioneer_discounted_price
      },
      total: userData.value.is_pioneer ? plan_pioneer_price : plan_price,
      subtotal: userData.value.is_pioneer ? cheap_plan.pioneer_price : cheap_plan.price,
      discount: userData.value.is_pioneer ? cheap_plan.pioneer_price_discount : cheap_plan.price_discount,
      amount: 0
    })
    return
  }
  const card_thumbnail = plan.amount === 1000 ? userData.value.coffee_card_thumbnail_1kg : userData.value.coffee_card_thumbnail_250g
  const plan_price = plan.discounted_price !== null ? plan.discounted_price : plan.price
  const plan_pioneer_price = plan.pioneer_discounted_price !== null ? plan.pioneer_discounted_price : plan.pioneer_price
  updateUserData({
    plan: {
      name: plan.plan,
      sku: plan.coffee_plan_sku,
      price: plan.price,
      pioneer_price: plan.pioneer_price,
      coffee_type: userData.value.coffee_type,
      discounted_price: plan.discounted_price,
      pioneer_discounted_price: plan.pioneer_discounted_price
    },
    coffee: {
      name: userData.value.coffee.name,
      sku: planCoffeeSku(plan, userData.value.coffee.sku),
      primary_image: undefined,
      card_thumbnail: card_thumbnail,
      label_artwork: userData.value.coffee_label_artwork,
    },
    total: userData.value.is_pioneer ? plan_pioneer_price : plan_price,
    subtotal: userData.value.is_pioneer ? plan.pioneer_price : plan.price,
    discount: userData.value.is_pioneer ? plan.pioneer_price_discount : plan.price_discount,
    amount: plan.amount,
  })

  GTM.funnelProgress(7, 'pick_amount', {
    range: userData.value.plan.plan,
    amount: userData.value.amount,
    range_sku: userData.value.plan.coffee_plan_sku,
    coffee: userData.value.coffee.name,
    coffee_sku: userData.value.coffee.sku,
  }, 'bag')
  if(funnel_version.value === 'D'){
    GTM.funnelProgress(8, 'pick_freq', { frequency: 28 }, 'bag')
    GTM.funnelProgress(9, 'complete_funnel', {}, 'bag')
    setPlanCache(6, true)
  } else {
    setPlanCache(5, true)
  }
}

onMounted(() => {

})
</script>

<script>
export default {
  name: 'BagSize',
}
</script>

<style lang="scss" scoped>
.bag-size-section {
  padding-top: 30px;
  padding-bottom: 50px;
}
</style>
